import type {ReactElement} from 'react';
import {useContext} from 'react';
import {MODALS_IDS} from '@Components/modal-container';
import {closeModal} from '@Components/modal-container/modal-container-reducer';
import {emailMakerContext} from '@EmailMakerReact/email-maker-react.context';
import {EMAIL_MAKER_PROPS} from '@EmailMakerReact/email-maker-react.constants';
import {EmailMakerBottomSheet} from '@EmailMakerReact/components/email-maker-bottom-sheet';
import {useCloseSheetOnCanvasInteraction} from '@EmailMakerReact/hooks/use-close-sheet-on-canvas-interaction';
import {updateColumnsPreset} from '@EmailMakerReact/lib/email-maker-table-library';
import {resolveColumnsPresetId} from '@EmailMakerReact/layout/email-maker-right-panel/email-maker-right-panel.util';
import {ColumnsAllView} from '@EmailMakerReact/layout/email-maker-right-panel/views/columns-all-view/columns-all-view';
import {useAppDispatch, useAppSelector} from '@/hooks';

const PANEL_ID = 'email-maker-columns-all-sheet-panel';

/** Pre-mounted inside the editor context so editor/selection are non-null when the sheet opens. */
export function EmailMakerColumnsAllSheet(): ReactElement | null {
  const {editor, state} = useContext(emailMakerContext);
  const dispatch = useAppDispatch();
  const isOpen = useAppSelector((s) => s.modals.modalsHashmap[MODALS_IDS.EMAIL_MAKER_COLUMNS_ALL_SHEET]?.isOpen ?? false);

  const selected = state.selectedComponent;

  useCloseSheetOnCanvasInteraction(isOpen ? editor : null, MODALS_IDS.EMAIL_MAKER_COLUMNS_ALL_SHEET, {
    skipSelectedCid: selected?.cid,
  });

  if (!isOpen || !selected || !editor) {
    return null;
  }

  const selectedPresetId = resolveColumnsPresetId(selected.get(EMAIL_MAKER_PROPS.COLUMNS_PRESET));

  function handlePresetSelect(presetId: number): void {
    if (!editor || !selected) {
      return;
    }
    selected.set(EMAIL_MAKER_PROPS.COLUMNS_PRESET, presetId);
    updateColumnsPreset(editor, presetId, selected);
    dispatch(closeModal(MODALS_IDS.EMAIL_MAKER_COLUMNS_ALL_SHEET));
  }

  function handleBack(): void {
    dispatch(closeModal(MODALS_IDS.EMAIL_MAKER_COLUMNS_ALL_SHEET));
  }

  return (
    <EmailMakerBottomSheet modalId={MODALS_IDS.EMAIL_MAKER_COLUMNS_ALL_SHEET} panelId={PANEL_ID} showDragHandle={false}>
      <ColumnsAllView component={selected} onBack={handleBack} onPresetSelect={handlePresetSelect} selectedPresetId={selectedPresetId} />
    </EmailMakerBottomSheet>
  );
}
